/**
 * Страница расписания тренера с календарем
 */
import { useState } from 'react';
import { useTrainerBookings } from '@shared/hooks/useTrainerBookings';
import { useAuthStore } from '@shared/store/authStore';

function formatDate(date: Date): string {
  // Используем локальную дату без конвертации в UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const WEEKDAYS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];

export default function SchedulePage() {
  const { user } = useAuthStore();
  const trainer = user;
  const { todayBookings: allBookings, isLoading } = useTrainerBookings();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0);

  // Получаем неделю с учетом смещения
  const getWeekDays = (offset: number = 0) => {
    const today = new Date();
    const days = [];
    const startOffset = offset * 7;
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + startOffset + i);
      days.push(date);
    }
    return days;
  };

  const weekDays = getWeekDays(weekOffset);

  const goToPreviousWeek = () => {
    setWeekOffset(prev => prev - 1);
  };

  const goToNextWeek = () => {
    setWeekOffset(prev => prev + 1);
  };

  const goToToday = () => {
    setWeekOffset(0);
    setSelectedDate(new Date());
  };

  // Получаем записи для выбранной даты
  const selectedDateStr = formatDate(selectedDate);
  const bookingsForSelectedDate = allBookings.filter(
    (booking) => booking.date === selectedDateStr
  );

  // Подсчитываем записи для каждого дня
  const bookingsCountByDate = allBookings.reduce((acc, booking) => {
    acc[booking.date] = (acc[booking.date] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4 sm:space-y-6">
        {/* Календарь */}
        <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-bold text-white/90">Календарь</h3>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={goToPreviousWeek}
                className="p-1.5 sm:p-2 hover:bg-slate-700/50 rounded-lg transition text-slate-300"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToToday}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-amber-400 hover:bg-amber-500/20 rounded-lg transition font-medium"
              >
                Сегодня
              </button>
              <button
                onClick={goToNextWeek}
                className="p-1.5 sm:p-2 hover:bg-slate-700/50 rounded-lg transition text-slate-300"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Недельный календарь */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {weekDays.map((date, index) => {
              const dateStr = formatDate(date);
              const bookingsCount = bookingsCountByDate[dateStr] || 0;
              const isToday = formatDate(date) === formatDate(new Date());
              const isSelected = formatDate(date) === formatDate(selectedDate);

              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className="text-center focus:outline-none"
                >
                  <div className="text-[10px] sm:text-xs text-slate-400 mb-1">{WEEKDAYS[date.getDay()]}</div>
                  <div className={`w-full h-8 sm:h-10 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold transition-all ${
                    isSelected
                      ? 'bg-amber-500 text-white ring-2 ring-amber-400/50 shadow-lg shadow-amber-500/30'
                      : isToday
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/80'
                  }`}>
                    {date.getDate()}
                  </div>
                  <div className="mt-1 text-xs">
                    {bookingsCount > 0 ? '🔴' : '🟢'}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="text-[10px] sm:text-xs text-slate-400 flex gap-3 sm:gap-4 justify-center mt-3 sm:mt-4">
            <span>🟢 Свободен</span>
            <span>🔴 Есть записи</span>
          </div>
        </div>

        {/* Записи на выбранную дату */}
        <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 border border-slate-700/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h2 className="text-base sm:text-lg font-bold text-white/90">
              Записи на {selectedDate.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </h2>
            <span className="text-xs sm:text-sm text-slate-400">
              {bookingsForSelectedDate.length} {bookingsForSelectedDate.length === 1 ? 'запись' : 'записей'}
            </span>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-slate-300">
              Загрузка...
            </div>
          ) : bookingsForSelectedDate.length === 0 ? (
            <div className="text-center py-6 sm:py-8">
              <svg
                className="w-12 h-12 sm:w-16 sm:h-16 text-slate-600 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-slate-400 text-sm sm:text-base">На эту дату нет записей</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {bookingsForSelectedDate
                .sort((a, b) => a.timeFrom.localeCompare(b.timeFrom))
                .map((booking) => (
                  <div
                    key={booking.id}
                    className={`
                      p-3 sm:p-4 rounded-xl border-2 transition-all
                      ${
                        booking.status === 'completed'
                          ? 'bg-slate-700/30 border-slate-600/50'
                          : booking.status === 'cancelled'
                          ? 'bg-red-500/10 border-red-500/30'
                          : 'bg-amber-500/10 border-amber-500/30'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-sm sm:text-lg font-bold text-white">
                            {booking.timeFrom} - {booking.timeTo}
                          </span>
                          <span
                            className={`
                              text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 rounded-full font-medium
                              ${
                                booking.status === 'completed'
                                  ? 'bg-slate-600 text-slate-200'
                                  : booking.status === 'cancelled'
                                  ? 'bg-red-500/20 text-red-300'
                                  : 'bg-amber-500/20 text-amber-300'
                              }
                            `}
                          >
                            {booking.status === 'completed'
                              ? 'Завершено'
                              : booking.status === 'cancelled'
                              ? 'Отменено'
                              : 'Запланировано'}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <p className="font-medium text-white text-sm sm:text-base">
                            {booking.serviceName}
                          </p>
                          {booking.type === 'group' && (
                            <span className="text-[10px] sm:text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full font-medium">
                              Группа
                            </span>
                          )}
                          {booking.type === 'court' && (
                            <span className="text-[10px] sm:text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-medium">
                              Корт
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-slate-300">
                          <span className="flex items-center gap-1">
                            {booking.type === 'group' ? (
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            )}
                            {booking.clientName}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            {booking.duration} мин
                          </span>
                        </div>
                      </div>

                      {booking.status === 'upcoming' && (
                        <button className="ml-3 p-1.5 sm:p-2 text-slate-400 hover:text-slate-300 transition">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
  );
}
